<template>
  <b-overlay rounded="sm">
    <b-container fluid>
      <h4 class="font-weight-bold pt-4 align-middle">Shared With You</h4>

      <b-row align-h="between" align-v="center">
        <b-col cols="12" md="5" lg="4" class="p-3 mb-0">
          <b-form-input
            v-model.trim="searchSharedToYou"
            placeholder="Search project name..."
            debounce="500"
          />
        </b-col>
      </b-row>

      <!-- Mobile: compact shared-with-you rows (hidden on md+) -->
      <div class="d-md-none pb-2">
        <div v-if="isLoadingSharedToYou" class="text-center py-3 text-primary">
          <digger-loader />
        </div>
        <template v-else>
          <div
            v-for="item in sharedToYouProjects"
            :key="item.id"
            class="mobile-shared-item"
            :class="[
              sharedToYouRowClass(item),
              isSharedRowOpenable(item) ? 'c-pointer' : '',
            ]"
            @click="openSharedProject(item)"
          >
            <div class="d-flex align-items-start">
              <div class="flex-grow-1" style="min-width: 0">
                <div class="d-flex align-items-center justify-content-between">
                  <span class="mobile-shared__name">{{
                    item.sharer_company_name || '-'
                  }}</span>
                  <span class="ml-2 flex-shrink-0 d-none d-sm-inline">
                    <b-badge
                      v-if="expiryStateOf(item) === 'expired'"
                      pill
                      variant="danger"
                      class="shared-status-badge"
                      >Expired</b-badge
                    >
                    <b-badge
                      v-else-if="expiryStateOf(item) === 'expiring_soon'"
                      pill
                      variant="warning"
                      class="shared-status-badge shared-status-badge--soon"
                      >{{ statusLabel(item) }}</b-badge
                    >
                    <span v-else class="mobile-shared__status">{{
                      statusLabel(item)
                    }}</span>
                  </span>
                </div>
                <div class="mobile-shared__meta">
                  {{ (item.project && item.project.name) || '-' }}
                </div>
                <div class="mobile-shared__sub d-none d-sm-block">
                  {{
                    item.sharer && item.sharer.full_name
                      ? 'By ' + item.sharer.full_name
                      : ''
                  }}<template v-if="item.created_at"
                    >{{ item.sharer && item.sharer.full_name ? ' · ' : ''
                    }}{{ formatDate(item.created_at) }}</template
                  ><template v-if="item.expires_at">
                    → {{ formatDate(item.expires_at) }}</template
                  >
                </div>
              </div>
              <b-button
                variant="outline-light"
                class="mobile-shared__remove ml-2 flex-shrink-0"
                :disabled="removingSharedToYouId === item.id"
                @click.stop="confirmRemoveSharedToYou(item)"
              >
                <b-icon-x-circle-fill variant="danger" />
              </b-button>
            </div>
          </div>
          <div
            v-if="!sharedToYouProjects || !sharedToYouProjects.length"
            class="text-center py-4 text-muted"
          >
            No shared projects found.
          </div>
        </template>
      </div>

      <!-- Desktop: full table (hidden below md) -->
      <div class="d-none d-md-block">
        <b-table
          id="shared-projects"
          class="mt-2"
          responsive
          hover
          show-empty
          :busy="isLoadingSharedToYou"
          :fields="fieldsSharedToYou"
          :items="sharedToYouProjects"
          :tbody-tr-class="sharedToYouRowClass"
          @row-clicked="openSharedProject"
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <digger-loader />
            </div>
          </template>

          <template #cell(status)="{ item }">
            <span class="shared-status-cell">
              <b-badge
                v-if="expiryStateOf(item) === 'expired'"
                pill
                variant="danger"
                class="shared-status-badge"
                >Expired</b-badge
              >
              <b-badge
                v-else-if="expiryStateOf(item) === 'expiring_soon'"
                pill
                variant="warning"
                class="shared-status-badge shared-status-badge--soon"
                >{{ statusLabel(item) }}</b-badge
              >
              <template v-else>{{ statusLabel(item) }}</template>
            </span>
          </template>

          <template #cell(remove)="{ item }">
            <b-button
              variant="outline-light"
              style="height: 32px"
              class="px-2"
              :disabled="removingSharedToYouId === item.id"
              :aria-label="
                'Remove shared project ' +
                (item.project && item.project.name
                  ? item.project.name
                  : 'project')
              "
              @click.stop="confirmRemoveSharedToYou(item)"
            >
              <b-icon-x-circle-fill variant="danger" />
            </b-button>
          </template>
        </b-table>
      </div>

      <b-pagination
        v-if="sharedToYouTotal > perPage"
        v-model="currentPageSharedToYou"
        :total-rows="sharedToYouTotal"
        :per-page="perPage"
        aria-controls="shared-projects"
        align="center"
      />

      <p v-if="sharedToYouProjectsMeta" align="center">
        Showing {{ sharedToYouProjects.length }} out of total of
        {{ sharedToYouProjectsMeta.total }} projects
      </p>

      <h4 class="font-weight-bold pt-4 align-middle">Shared By You</h4>

      <b-row align-h="between" align-v="center">
        <b-col cols="12" md="5" lg="4" class="p-3 mb-0">
          <b-form-input
            v-model.trim="searchSharedByYou"
            placeholder="Search project name..."
            debounce="500"
          />
        </b-col>
        <!-- Desktop: full Share Project button -->
        <b-col cols="auto" class="d-none d-md-block">
          <b-row align-h="end">
            <b-button
              v-if="canShareProject"
              variant="primary"
              @click="ShareProject(true)"
              class="mr-3 px-3"
            >
              <b-icon-plus font-scale="1.5" class="align-middle"></b-icon-plus>
              <span class="align-middle">Share Project</span>
            </b-button>
          </b-row>
        </b-col>
        <!-- Mobile: full text Share Project button -->
        <b-col cols="auto" class="d-flex d-md-none pr-3">
          <b-button
            v-if="canShareProject"
            variant="primary"
            class="pl-1 pr-2"
            style="
              font-size: 0.7rem;
              padding-top: 0.15rem;
              padding-bottom: 0.15rem;
              line-height: 1.4;
            "
            @click="ShareProject(true)"
          >
            <b-icon-plus
              style="font-size: 0.8rem"
              class="align-middle"
            ></b-icon-plus>
            <span class="align-middle">Share Project</span>
          </b-button>
        </b-col>
      </b-row>

      <share-project-modal
        :showModal="isSharingProject"
        :submitting="isSubmittingShare"
        :shareAlertMessage="shareModalAlert"
        :shareAlertVariant="shareModalAlertVariant"
        @clearShareAlert="clearShareModalAlert"
        @onCloseShareProjectModal="isSharingProject = false"
        @onShareProjects="onShareProjects"
      />

      <share-project-duplicate-modal
        :visible="showDuplicateNotice"
        :project-names="duplicateNoticeNames"
        :subtitle="duplicateNoticeSubtitle"
        @close="closeDuplicateNotice"
      />

      <project-share-files-modal
        v-model="filesShareOpen"
        :project-id="filesShare && filesShare.project_id"
        :project-name="(filesShare && filesShare.project_name) || ''"
        :selected-ids="filesShareSelected"
        :busy="filesShareSaving"
        confirm-label="SAVE"
        @confirm="saveShareFiles"
      />

      <!-- Mobile: compact shared-by-you rows (hidden on md+) -->
      <div class="d-md-none pb-2 mt-2">
        <div v-if="isLoadingSharedByYou" class="text-center py-3 text-primary">
          <digger-loader />
        </div>
        <template v-else>
          <div
            v-for="item in sharedByYouProjects"
            :key="item.id"
            class="mobile-shared-item"
            :class="[
              sharedByYouRowClass(item),
              isOwnProjectRowOpenable(item) ? 'c-pointer' : '',
            ]"
            @click="openOwnProject(item)"
          >
            <div class="d-flex align-items-start">
              <div class="flex-grow-1" style="min-width: 0">
                <div class="d-flex align-items-center justify-content-between">
                  <span class="mobile-shared__name">{{
                    item.receiver_company_name ||
                    sharedToRecipientLabel(item) ||
                    '-'
                  }}</span>
                  <span class="ml-2 flex-shrink-0 d-none d-sm-inline">
                    <b-badge
                      v-if="expiryStateOf(item) === 'expired'"
                      pill
                      variant="danger"
                      class="shared-status-badge"
                      >Expired</b-badge
                    >
                    <b-badge
                      v-else-if="expiryStateOf(item) === 'expiring_soon'"
                      pill
                      variant="warning"
                      class="shared-status-badge shared-status-badge--soon"
                      >{{ statusLabel(item) }}</b-badge
                    >
                    <span v-else class="mobile-shared__status">{{
                      statusLabel(item)
                    }}</span>
                  </span>
                </div>
                <div class="mobile-shared__meta">
                  {{ (item.project && item.project.name) || '-' }}
                </div>
                <div v-if="canEditShareFiles" class="mobile-shared__sub">
                  <button
                    type="button"
                    class="shared-files-link"
                    @click.stop="openShareFiles(item)"
                  >
                    {{ sharedFilesText(item) }}
                  </button>
                </div>
                <div class="mobile-shared__sub d-none d-sm-block">
                  <template v-if="sharedToRecipientLabel(item)"
                    >To {{ sharedToRecipientLabel(item)
                    }}<template v-if="item.created_at"> · </template></template
                  >{{ item.created_at ? formatDate(item.created_at) : ''
                  }}<template v-if="item.expires_at">
                    → {{ formatDate(item.expires_at) }}</template
                  >
                </div>
              </div>
              <b-button
                variant="outline-light"
                class="mobile-shared__remove ml-2 flex-shrink-0"
                :disabled="removingShareId === item.id"
                @click.stop="confirmRemoveShare(item)"
              >
                <b-icon-x-circle-fill variant="danger" />
              </b-button>
            </div>
          </div>
          <div
            v-if="!sharedByYouProjects || !sharedByYouProjects.length"
            class="text-center py-4 text-muted"
          >
            No shared projects found.
          </div>
        </template>
      </div>

      <!-- Desktop: full table (hidden below md) -->
      <div class="d-none d-md-block">
        <b-table
          id="shared-projects-by-you"
          class="mt-2"
          responsive
          hover
          show-empty
          :busy="isLoadingSharedByYou"
          :fields="fieldsSharedByYou"
          :items="sharedByYouProjects"
          :tbody-tr-class="sharedByYouRowClass"
          @row-clicked="openOwnProject"
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <digger-loader />
            </div>
          </template>

          <template #cell(status)="{ item }">
            <span class="shared-status-cell">
              <b-badge
                v-if="expiryStateOf(item) === 'expired'"
                pill
                variant="danger"
                class="shared-status-badge"
                >Expired</b-badge
              >
              <b-badge
                v-else-if="expiryStateOf(item) === 'expiring_soon'"
                pill
                variant="warning"
                class="shared-status-badge shared-status-badge--soon"
                >{{ statusLabel(item) }}</b-badge
              >
              <template v-else>{{ statusLabel(item) }}</template>
            </span>
          </template>

          <template #cell(shared_files_count)="{ item }">
            <button
              v-if="canEditShareFiles"
              type="button"
              class="shared-files-link"
              :data-test="`edit-files-${item.id}`"
              :aria-label="'Choose the files shared for ' + shareLabel(item)"
              @click.stop="openShareFiles(item)"
            >
              {{ sharedFilesText(item) }}
            </button>
            <span v-else>{{ sharedFilesText(item) }}</span>
          </template>

          <template #cell(remove)="{ item }">
            <b-button
              variant="outline-light"
              style="height: 32px"
              class="px-2"
              :disabled="removingShareId === item.id"
              :aria-label="
                'Remove share for ' +
                (item.project && item.project.name
                  ? item.project.name
                  : 'project')
              "
              @click.stop="confirmRemoveShare(item)"
            >
              <b-icon-x-circle-fill variant="danger" />
            </b-button>
          </template>
        </b-table>
      </div>

      <b-pagination
        v-if="sharedByYouTotal > perPage"
        v-model="currentPageSharedByYou"
        :total-rows="sharedByYouTotal"
        :per-page="perPage"
        aria-controls="shared-projects-by-you"
        align="center"
      />
      <p v-if="sharedByYouProjectsMeta" align="center">
        Showing {{ sharedByYouProjects.length }} out of total of
        {{ sharedByYouProjectsMeta.total }} projects
      </p>
    </b-container>
  </b-overlay>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ShareProjectModal from '@/components/modals/ShareProjectModal.vue'
import ShareProjectDuplicateModal from '@/components/modals/ShareProjectDuplicateModal.vue'
import ProjectShareFilesModal from '@/components/projects/ProjectShareFilesModal'

function extractShareApiMessage(reason) {
  if (!reason) return ''
  const data = reason.response && reason.response.data
  return (data && data.message) || ''
}

function isAlreadySharedApiMessage(msg) {
  return typeof msg === 'string' && /already been shared/i.test(msg)
}

export default {
  components: {
    ShareProjectModal,
    ProjectShareFilesModal,
    ShareProjectDuplicateModal,
  },

  data() {
    return {
      isLoadingSharedToYou: false,
      isLoadingSharedByYou: false,
      isSharingProject: false,
      isSubmittingShare: false,
      searchSharedToYou: '',
      searchSharedByYou: '',
      shareModalAlert: '',
      shareModalAlertVariant: 'danger',
      showDuplicateNotice: false,
      duplicateNoticeNames: [],
      duplicateNoticeSubtitle: '',
      removingShareId: null,
      removingSharedToYouId: null,
      // Editing which project files an existing share includes.
      filesShare: null,
      filesShareSelected: [],
      filesShareOpen: false,
      filesShareSaving: false,
      perPage: 10,
      currentPageSharedToYou: 1,
      currentPageSharedByYou: 1,
    }
  },

  computed: {
    fieldsSharedToYou() {
      return [
        {
          key: 'sharer_company_name',
          label: 'Company',
          sortable: true,
          thStyle: { width: '20%' },
        },
        {
          key: 'project.name',
          label: 'Project',
          sortable: true,
          thStyle: { width: '18%' },
        },
        {
          key: 'project.tradies_count',
          label: 'Tradies',
          sortable: true,
          thClass: 'text-nowrap text-center',
          tdClass: 'text-center',
          thStyle: { width: '6%' },
        },
        {
          key: 'project_approved_tradies_count',
          label: 'Project Approved Tradies',
          sortable: true,
          thClass: 'text-nowrap text-center',
          tdClass: 'text-center',
          thStyle: { width: '12%' },
          formatter: (value) => (value == null ? 0 : value),
        },
        {
          key: 'sharer.full_name',
          label: 'Shared by',
          sortable: true,
          thStyle: { width: '14%' },
        },
        {
          key: 'created_at',
          label: 'Shared on',
          sortable: true,
          thClass: 'text-nowrap',
          tdClass: 'text-nowrap',
          thStyle: { width: '10%' },
          formatter: (_value, _key, item) =>
            this.formatDate(item && item.created_at),
        },
        {
          key: 'expires_at',
          label: 'Expires',
          sortable: true,
          thClass: 'text-nowrap',
          tdClass: 'text-nowrap',
          thStyle: { width: '10%' },
          formatter: (_value, _key, item) =>
            this.formatDate(item && item.expires_at),
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
          sortByFormatted: true,
          thClass: 'text-nowrap',
          tdClass: 'text-nowrap',
          thStyle: { width: '8%' },
          formatter: (_value, _key, item) => this.statusLabel(item),
        },
        {
          key: 'remove',
          label: 'Remove',
          class: 'text-center',
          thStyle: { width: '6%' },
        },
      ]
    },

    fieldsSharedByYou() {
      return [
        {
          key: 'receiver_company_name',
          label: 'Company',
          sortable: true,
          thStyle: { width: '20%' },
        },
        {
          key: 'project.name',
          label: 'Project',
          sortable: true,
          thStyle: { width: '18%' },
        },
        {
          key: 'project.tradies_count',
          label: 'Tradies',
          sortable: true,
          thClass: 'text-nowrap text-center',
          tdClass: 'text-center',
          thStyle: { width: '6%' },
        },
        {
          key: 'project_approved_tradies_count',
          label: 'Project Approved Tradies',
          sortable: true,
          thClass: 'text-nowrap text-center',
          tdClass: 'text-center',
          thStyle: { width: '12%' },
          formatter: (value) => (value == null ? 0 : value),
        },
        {
          key: 'shared_files_count',
          label: 'Files',
          thClass: 'text-nowrap',
          tdClass: 'text-nowrap',
          thStyle: { width: '8%' },
        },
        {
          key: 'receiver.full_name',
          label: 'Shared to',
          sortable: true,
          sortByFormatted: true,
          thStyle: { width: '14%' },
          formatter: (_value, _key, item) => this.sharedToRecipientLabel(item),
        },
        {
          key: 'created_at',
          label: 'Shared on',
          sortable: true,
          thClass: 'text-nowrap',
          tdClass: 'text-nowrap',
          thStyle: { width: '10%' },
          formatter: (_value, _key, item) =>
            this.formatDate(item && item.created_at),
        },
        {
          key: 'expires_at',
          label: 'Expires',
          sortable: true,
          thClass: 'text-nowrap',
          tdClass: 'text-nowrap',
          thStyle: { width: '10%' },
          formatter: (_value, _key, item) =>
            this.formatDate(item && item.expires_at),
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
          sortByFormatted: true,
          thClass: 'text-nowrap',
          tdClass: 'text-nowrap',
          thStyle: { width: '8%' },
          formatter: (_value, _key, item) => this.statusLabel(item),
        },
        {
          key: 'remove',
          label: 'Remove',
          class: 'text-center',
          thStyle: { width: '6%' },
        },
      ]
    },

    ...mapGetters('project', {
      sharedToYouProjects: 'sharedToMeProjects',
      sharedToYouProjectsMeta: 'sharedToMeProjectsMeta',
      sharedByYouProjects: 'sharedByMeProjects',
      sharedByYouProjectsMeta: 'sharedByMeProjectsMeta',
      projects: 'projects',
    }),

    ...mapGetters('auth', ['user', 'hasPermission']),

    // Choosing which project files a share includes needs permission to see
    // them (the API enforces the same).
    canEditShareFiles() {
      return (
        !!this.user &&
        this.user.user_type === 'business_admin' &&
        this.hasPermission('projects.files.view')
      )
    },

    canShareProject() {
      if (!this.user || this.user.user_type !== 'business_admin') return false
      return this.hasPermission('projects.share')
    },

    sharedToYouTotal() {
      const meta = this.sharedToYouProjectsMeta
      if (meta && meta.total != null) return meta.total
      return (this.sharedToYouProjects || []).length
    },

    sharedByYouTotal() {
      const meta = this.sharedByYouProjectsMeta
      if (meta && meta.total != null) return meta.total
      return (this.sharedByYouProjects || []).length
    },
  },

  watch: {
    searchSharedToYou() {
      this.currentPageSharedToYou = 1
      this.fetchSharedToYouProjects()
    },
    searchSharedByYou() {
      this.currentPageSharedByYou = 1
      this.fetchSharedByYouProjects()
    },
    currentPageSharedToYou() {
      this.fetchSharedToYouProjects()
    },
    currentPageSharedByYou() {
      this.fetchSharedByYouProjects()
    },
  },

  methods: {
    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },

    sharedToYouRowClass(item) {
      if (!item) return ''
      const classes = []
      if (this.isSharedRowOpenable(item)) classes.push('cursor-pointer')
      const expiryClass = this.expiryRowClass(item)
      if (expiryClass) classes.push(expiryClass)
      return classes.join(' ')
    },

    sharedByYouRowClass(item) {
      if (!item) return ''
      const classes = []
      if (this.isOwnProjectRowOpenable(item)) classes.push('cursor-pointer')
      const expiryClass = this.expiryRowClass(item)
      if (expiryClass) classes.push(expiryClass)
      return classes.join(' ')
    },

    isOwnProjectRowOpenable(item) {
      return !!(item && item.project && item.project.id != null)
    },

    openOwnProject(item) {
      if (!this.isOwnProjectRowOpenable(item)) return
      this.$router.push({
        name: 'projects.view-page',
        params: { id: String(item.project.id) },
      })
    },

    expiryStateOf(item) {
      if (!item || !item.expires_at) return 'none'
      const expires = new Date(item.expires_at)
      const now = new Date()
      if (now >= expires) return 'expired'
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
      if (expires - now <= sevenDaysMs) return 'expiring_soon'
      return 'active'
    },

    expiryRowClass(item) {
      const state = this.expiryStateOf(item)
      if (state === 'expired') return 'shared-row-expired'
      if (state === 'expiring_soon') return 'shared-row-expiring-soon'
      return ''
    },

    statusLabel(item) {
      if (!item) return ''
      const status =
        item.status != null ? String(item.status).toLowerCase() : ''
      if (status === 'pending') return 'Pending invite'
      const state = this.expiryStateOf(item)
      if (state === 'expired') return 'Expired'
      if (state === 'expiring_soon') {
        const expires = new Date(item.expires_at)
        const now = new Date()
        const days = Math.max(
          1,
          Math.ceil((expires - now) / (24 * 60 * 60 * 1000)),
        )
        return `Expires in ${days} day${days === 1 ? '' : 's'}`
      }
      return 'Active'
    },

    isSharedRowOpenable(item) {
      if (!item || !item.project || item.project.id == null) return false
      const status =
        item.status != null ? String(item.status).toLowerCase() : null
      if (status && status !== 'active' && status !== 'accepted') return false
      return true
    },

    openSharedProject(item) {
      if (!this.isSharedRowOpenable(item)) return
      if (this.expiryStateOf(item) === 'expired') {
        const projectName =
          (item.project && item.project.name) || 'this project'
        const companyName = item.sharer_company_name || 'the sharing company'
        this.$bvModal.msgBoxOk(
          `Your access to "${projectName}" has expired. Please ask ${companyName} to share the project again to regain access.`,
          {
            title: 'Shared project expired',
            size: 'md',
            centered: true,
            okVariant: 'primary',
            okTitle: 'OK',
            buttonSize: 'md',
            modalClass: 'remove-share-confirm-modal',
            headerClass: 'remove-share-confirm-header border-bottom-0',
            bodyClass: 'remove-share-confirm-body',
            footerClass:
              'remove-share-confirm-footer border-top-0 justify-content-end',
          },
        )
        return
      }
      this.$router.push({
        name: 'projects.shared-view-page',
        params: { shareId: String(item.id) },
      })
    },

    sharedToRecipientLabel(item) {
      const r = item && item.receiver
      const name = r && r.full_name != null ? String(r.full_name).trim() : ''
      if (name) return name
      const nestedEmail = r && r.email != null ? String(r.email).trim() : ''
      if (nestedEmail) return nestedEmail
      const shareEmail =
        item && item.receiver_email != null
          ? String(item.receiver_email).trim()
          : ''
      return shareEmail
    },

    ...mapActions('project', {
      getSharedToYouProjects: 'getSharedToMeProjects',
      getSharedByYouProjects: 'getSharedByMeProjects',
      createProjectShare: 'createProjectShare',
      getProjectShareFiles: 'getProjectShareFiles',
      updateProjectShareFiles: 'updateProjectShareFiles',
      deleteProjectShare: 'deleteProjectShare',
      deleteReceivedProjectShare: 'deleteReceivedProjectShare',
    }),

    shareLabel(item) {
      const project = item && item.project && item.project.name
      return project || 'project'
    },

    // "3 files", "No files": what this share includes from the project's Files tab.
    sharedFilesText(item) {
      const n = Number(item && item.shared_files_count) || 0
      if (n === 0) return 'No files'
      return n === 1 ? '1 file' : `${n} files`
    },

    async openShareFiles(item) {
      try {
        const current = await this.getProjectShareFiles(item.id)
        this.filesShare = {
          id: item.id,
          project_id: item.project_id,
          project_name: (item.project && item.project.name) || '',
          recipient_name: this.sharedToRecipientLabel(item) || '',
        }
        this.filesShareSelected = current.file_ids || []
        this.filesShareOpen = true
      } catch (err) {
        this.$bvToast.toast(
          extractShareApiMessage(err) ||
            'We couldn’t load the files for this share. Please try again.',
          { title: 'Share Project', variant: 'danger', solid: true },
        )
      }
    },

    // The recipient sees a changed selection straight away, so warn before
    // files that were shared stop being shared. Resolves true to carry on.
    async confirmFilesNoLongerShared(ids) {
      const removed = (this.filesShareSelected || []).filter(
        (id) => !ids.includes(id),
      )
      if (removed.length === 0) return true
      const recipient = this.filesShare.recipient_name || 'the recipient'
      const files =
        removed.length === 1
          ? '1 file that was'
          : `${removed.length} files that were`
      try {
        return await this.$bvModal.msgBoxConfirm(
          `You're removing ${files} previously shared. ${recipient} will no longer be able to see ${
            removed.length === 1 ? 'it' : 'them'
          } straight away.`,
          {
            title: 'Some files will no longer be shared',
            size: 'md',
            centered: true,
            okVariant: 'primary',
            cancelVariant: 'outline-secondary',
            okTitle: 'CONTINUE',
            cancelTitle: 'CANCEL',
            buttonSize: 'md',
            modalClass: 'remove-share-confirm-modal',
            headerClass: 'remove-share-confirm-header border-bottom-0',
            bodyClass: 'remove-share-confirm-body',
            footerClass:
              'remove-share-confirm-footer border-top-0 justify-content-end',
          },
        )
      } catch (e) {
        return false
      }
    },

    async saveShareFiles(ids) {
      if (!this.filesShare || this.filesShareSaving) return
      if (!(await this.confirmFilesNoLongerShared(ids))) return
      this.filesShareSaving = true
      try {
        const result = await this.updateProjectShareFiles({
          shareId: this.filesShare.id,
          fileIds: ids,
        })
        const count = (result.file_ids || []).length
        const row = (this.sharedByYouProjects || []).find(
          (p) => p.id === this.filesShare.id,
        )
        if (row) this.$set(row, 'shared_files_count', count)
        this.filesShareOpen = false
        this.$bvToast.toast(
          count === 0
            ? 'No files are shared now.'
            : `${count} file${count === 1 ? ' is' : 's are'} shared now.`,
          { title: 'Share Project', variant: 'success', solid: true },
        )
      } catch (err) {
        const data = err && err.response && err.response.data
        const first =
          data && data.errors && data.errors.file_ids
            ? data.errors.file_ids[0]
            : ''
        this.$bvToast.toast(
          first ||
            (data && data.message) ||
            'We couldn’t save the shared files. Please try again.',
          { title: 'Share Project', variant: 'danger', solid: true },
        )
      } finally {
        this.filesShareSaving = false
      }
    },

    async confirmRemoveSharedToYou(item) {
      const projectName =
        item.project && item.project.name ? item.project.name : 'this project'
      const sharerName =
        (item.sharer && item.sharer.full_name) ||
        item.sharer_company_name ||
        'the sender'
      try {
        const ok = await this.$bvModal.msgBoxConfirm(
          `Remove "${projectName}" shared with you by ${sharerName}?`,
          {
            title: 'Remove share',
            size: 'md',
            centered: true,
            okVariant: 'primary',
            cancelVariant: 'outline-secondary',
            okTitle: 'YES',
            cancelTitle: 'CANCEL',
            buttonSize: 'md',
            modalClass: 'remove-share-confirm-modal',
            headerClass: 'remove-share-confirm-header border-bottom-0',
            bodyClass: 'remove-share-confirm-body',
            footerClass:
              'remove-share-confirm-footer border-top-0 justify-content-end',
          },
        )
        if (!ok) return
      } catch (e) {
        return
      }

      this.removingSharedToYouId = item.id
      try {
        await this.deleteReceivedProjectShare(item.id)
        this.$bvToast.toast('Shared project removed.', {
          title: 'Shared Project',
          variant: 'success',
          solid: true,
        })
        await this.fetchSharedToYouProjects()
      } catch (err) {
        const data = err && err.response && err.response.data
        const msg =
          (data && data.message) ||
          (err && err.message) ||
          'Could not remove this shared project. Please try again.'
        this.$bvToast.toast(msg, {
          title: 'Shared Project',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.removingSharedToYouId = null
      }
    },

    async confirmRemoveShare(item) {
      const projectName =
        item.project && item.project.name ? item.project.name : 'this project'
      const recipientName =
        this.sharedToRecipientLabel(item) || 'this recipient'
      try {
        const ok = await this.$bvModal.msgBoxConfirm(
          `Stop sharing "${projectName}" with ${recipientName}?`,
          {
            title: 'Remove share',
            size: 'md',
            centered: true,
            okVariant: 'primary',
            cancelVariant: 'outline-secondary',
            okTitle: 'YES',
            cancelTitle: 'CANCEL',
            buttonSize: 'md',
            modalClass: 'remove-share-confirm-modal',
            headerClass: 'remove-share-confirm-header border-bottom-0',
            bodyClass: 'remove-share-confirm-body',
            footerClass:
              'remove-share-confirm-footer border-top-0 justify-content-end',
          },
        )
        if (!ok) return
      } catch (e) {
        return
      }

      this.removingShareId = item.id
      try {
        await this.deleteProjectShare(item.id)
        this.$bvToast.toast('Share removed.', {
          title: 'Share Project',
          variant: 'success',
          solid: true,
        })
        this.$emit('shares-changed')
        await this.fetchSharedByYouProjects()
      } catch (err) {
        const data = err && err.response && err.response.data
        const msg =
          (data && data.message) ||
          (err && err.message) ||
          'Could not remove this share. Please try again.'
        this.$bvToast.toast(msg, {
          title: 'Share Project',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.removingShareId = null
      }
    },

    async fetchSharedToYouProjects() {
      this.isLoadingSharedToYou = true
      await this.getSharedToYouProjects({
        search: this.searchSharedToYou || undefined,
        page: this.currentPageSharedToYou,
        limit: this.perPage,
      }).finally(() => {
        this.isLoadingSharedToYou = false
      })
    },

    // Returns a boolean success flag (rather than letting a rejection
    // propagate) so every caller — the search/pagination watchers, mounted(),
    // and the post-submit refresh in onShareProjects() — is guaranteed this
    // never throws. Without that, a failure here used to bubble out of
    // onShareProjects() and skip the `isSubmittingShare = false` line right
    // after it, leaving the share modal's Submit/Cancel buttons permanently
    // disabled.
    async fetchSharedByYouProjects() {
      this.isLoadingSharedByYou = true
      try {
        await this.getSharedByYouProjects({
          search: this.searchSharedByYou || undefined,
          page: this.currentPageSharedByYou,
          limit: this.perPage,
        })
        return true
      } catch (err) {
        return false
      } finally {
        this.isLoadingSharedByYou = false
      }
    },

    ShareProject(status) {
      this.isSharingProject = status
      if (status) {
        this.shareModalAlert = ''
        this.shareModalAlertVariant = 'danger'
        this.closeDuplicateNotice()
      }
    },

    closeDuplicateNotice() {
      this.showDuplicateNotice = false
      this.duplicateNoticeNames = []
      this.duplicateNoticeSubtitle = ''
    },

    clearShareModalAlert() {
      this.shareModalAlert = ''
      this.shareModalAlertVariant = 'danger'
    },

    buildShareFailureCopy(projectIds, results, projectSelection, successCount) {
      const idToName = new Map()
      if (projectSelection && projectSelection.length) {
        projectSelection.forEach(({ id, name }) => {
          idToName.set(id, name || `Project ${id}`)
        })
      }
      ;(this.projects || []).forEach((p) => {
        if (p && p.id != null && !idToName.has(p.id)) {
          idToName.set(p.id, p.name || `Project ${p.id}`)
        }
      })

      const alreadySharedNames = []
      const otherLines = []

      projectIds.forEach((projectId, index) => {
        const r = results[index]
        if (r.status !== 'rejected') return
        const msg = extractShareApiMessage(r.reason)
        const name = idToName.get(projectId) || `Project ${projectId}`
        if (isAlreadySharedApiMessage(msg)) {
          alreadySharedNames.push(name)
        } else {
          otherLines.push({ name, msg: msg || 'Could not be shared.' })
        }
      })

      const parts = []
      if (successCount > 0) {
        parts.push(
          `${successCount} of ${projectIds.length} project(s) shared successfully.`,
          '',
        )
      }
      if (alreadySharedNames.length > 0) {
        parts.push('These projects were already shared with this recipient:')
        alreadySharedNames.forEach((n) => parts.push(`• ${n}`))
      }
      if (otherLines.length > 0) {
        if (parts.length) parts.push('')
        parts.push('Could not share:')
        otherLines.forEach(({ name, msg }) => parts.push(`• ${name} - ${msg}`))
      }
      if (parts.length === 0) {
        const rej = results.find((x) => x.status === 'rejected')
        parts.push(
          extractShareApiMessage(rej && rej.reason) ||
            'Failed to share the selected projects. Please try again.',
        )
      }

      const modalMessage = parts.join('\n')
      const hasOther = otherLines.length > 0
      const hasDup = alreadySharedNames.length > 0
      const variant = hasOther ? 'danger' : hasDup ? 'warning' : 'danger'

      let toastMsg = modalMessage
      if (toastMsg.length > 200) {
        if (hasDup && !hasOther) {
          toastMsg = `${alreadySharedNames.length} project(s) were already shared. See the list above.`
        } else if (hasOther) {
          toastMsg =
            'Some projects could not be shared. See the list in the dialog.'
        } else {
          toastMsg = `${toastMsg.slice(0, 197)}…`
        }
      }

      return {
        modalMessage,
        variant,
        toastMsg,
        alreadySharedNames,
        otherLines,
        successCount,
        totalCount: projectIds.length,
      }
    },

    formatNonDuplicateAlert(detail) {
      const parts = []
      if (detail.otherLines.length > 0 && detail.successCount > 0) {
        parts.push(
          `${detail.successCount} of ${detail.totalCount} project(s) shared successfully.`,
          '',
        )
      }
      if (detail.otherLines.length > 0) {
        parts.push('Could not share:')
        detail.otherLines.forEach(({ name, msg }) =>
          parts.push(`• ${name} - ${msg}`),
        )
      }
      return parts.join('\n').trim()
    },

    async onShareProjects(payload) {
      const {
        email,
        project_ids: projectIds,
        recipient_status: recipientStatus,
        project_selection: projectSelection,
        project_files: projectFiles = {},
      } = payload
      if (!projectIds || projectIds.length === 0) return

      this.clearShareModalAlert()
      this.isSubmittingShare = true

      const results = await Promise.allSettled(
        projectIds.map((projectId) =>
          this.createProjectShare({
            project_id: projectId,
            receiver_email: email,
            // Opt-in: only projects with files chosen send any.
            ...(projectFiles[projectId] && projectFiles[projectId].length
              ? { file_ids: projectFiles[projectId] }
              : {}),
          }),
        ),
      )

      const failures = results.filter((r) => r.status === 'rejected')
      const successCount = results.length - failures.length

      if (failures.length === 0) {
        this.clearShareModalAlert()
        this.$bvToast.toast(
          recipientStatus === 'new'
            ? 'Invitation sent. The recipient will see these projects once they create an account.'
            : `${successCount} project${
                successCount === 1 ? '' : 's'
              } shared successfully.`,
          {
            title: 'Share Project',
            variant: 'success',
            solid: true,
          },
        )
        this.isSharingProject = false
      } else {
        const detail = this.buildShareFailureCopy(
          projectIds,
          results,
          projectSelection,
          successCount,
        )

        const hasDup = detail.alreadySharedNames.length > 0
        const hasOther = detail.otherLines.length > 0

        if (hasDup) {
          this.duplicateNoticeNames = detail.alreadySharedNames.slice()
          this.duplicateNoticeSubtitle =
            detail.successCount > 0
              ? `${detail.successCount} of ${detail.totalCount} project(s) shared successfully.`
              : ''
          this.showDuplicateNotice = true
        }

        if (hasOther) {
          this.shareModalAlert = hasDup
            ? this.formatNonDuplicateAlert(detail)
            : detail.modalMessage
          this.shareModalAlertVariant = 'danger'
        } else if (!hasDup) {
          this.shareModalAlert = detail.modalMessage
          this.shareModalAlertVariant = detail.variant
        } else {
          this.clearShareModalAlert()
        }

        if (!hasDup) {
          this.$bvToast.toast(detail.toastMsg, {
            title: 'Share Project',
            variant: detail.variant,
            solid: true,
          })
        }
      }

      if (successCount > 0) {
        this.$emit('shares-changed')
      }

      const refreshed = await this.fetchSharedByYouProjects()
      if (!refreshed) {
        this.$bvToast.toast(
          'Your share was submitted, but the list could not be refreshed. Please reload the page to see the latest shares.',
          {
            title: 'Share Project',
            variant: 'warning',
            solid: true,
          },
        )
      }
      this.isSubmittingShare = false
    },
  },

  mounted() {
    this.fetchSharedToYouProjects()
    this.fetchSharedByYouProjects()
  },
}
</script>

<style lang="scss">
// A text link, not a .btn (those have a fixed 56px height here).
.shared-files-link {
  padding: 0;
  border: 0;
  background: transparent;
  color: #006cff;
  font: inherit;
  cursor: pointer;

  &:hover,
  &:focus {
    text-decoration: underline;
    outline: none;
  }
}

.mobile-shared-item {
  padding: 0.65rem 1rem;
  border-bottom: 1px solid #e1e6ea;
  background: white;

  &.shared-row-expired {
    background-color: #f8d7da;
  }

  &.shared-row-expiring-soon {
    background-color: #fff3cd;
  }
}

.mobile-shared__name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #364052;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-shared__meta {
  font-size: 0.72rem;
  color: #8b95a3;
  margin-top: 0.15rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-shared__sub {
  font-size: 0.72rem;
  color: #8b95a3;
  margin-top: 0.1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-shared__status {
  font-size: 0.72rem;
  color: #8b95a3;
}

.mobile-shared__remove {
  height: 28px;
  width: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  flex-shrink: 0;
}

.mobile-shared__action-btn {
  height: 28px;
  width: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}

#shared-projects tbody tr.cursor-pointer,
#shared-projects-by-you tbody tr.cursor-pointer {
  cursor: pointer;
}

#shared-projects tbody tr.shared-row-expiring-soon,
#shared-projects-by-you tbody tr.shared-row-expiring-soon {
  background-color: #fff3cd;
}

#shared-projects tbody tr.shared-row-expiring-soon:hover,
#shared-projects-by-you tbody tr.shared-row-expiring-soon:hover {
  background-color: #ffeeba;
}

#shared-projects tbody tr.shared-row-expired,
#shared-projects-by-you tbody tr.shared-row-expired {
  background-color: #f8d7da;
}

#shared-projects tbody tr.shared-row-expired:hover,
#shared-projects-by-you tbody tr.shared-row-expired:hover {
  background-color: #f5c2c7;
}

.shared-status-cell {
  display: inline-block;
}

.shared-status-badge {
  font-weight: 600;
}

.shared-status-badge--soon {
  background-color: #fff3cd !important;
  color: #856404 !important;
  border: 1px solid #ffc107;
}

/* Msg box mounts on document — targets BootstrapVue message modal */
.remove-share-confirm-modal {
  .modal-header.remove-share-confirm-header {
    padding-bottom: 0.25rem !important;
    padding-top: 1rem !important;
  }

  .modal-body.remove-share-confirm-body {
    /* tighter gap under title */
    padding-top: 0.25rem !important;
    padding-bottom: 0.65rem !important;
  }

  .modal-footer.remove-share-confirm-footer {
    padding-top: 0 !important;
    padding-bottom: 0.4rem !important;
  }
}
</style>
